/**
 * useBuildingExternalContacts Hook
 * Manages a building's external-contact links (read + mutate).
 *
 * The read path reads users/userList directly (see buildingExternalContactsApi),
 * so the list loads for non-admins. Mutating actions are exposed for admin UI;
 * the calling component gates them on isAdmin.
 */
import { useState, useEffect, useCallback } from 'react';
import {
    buildingExternalContactsApi,
    BuildingExternalContactWithUser,
} from '../api/buildingExternalContactsApi';

interface UseBuildingExternalContactsReturn {
    contacts: BuildingExternalContactWithUser[];
    loading: boolean;
    error: string | null;
    submitting: boolean;
    refetch: () => Promise<void>;
    addContact: (externalUserId: string, buildingNote?: string) => Promise<void>;
    removeContact: (externalUserId: string) => Promise<void>;
    updateNote: (externalUserId: string, note: string) => Promise<void>;
    clearError: () => void;
}

export const useBuildingExternalContacts = (
    buildingKey: string
): UseBuildingExternalContactsReturn => {
    const [contacts, setContacts] = useState<BuildingExternalContactWithUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const fetchContacts = useCallback(async () => {
        if (!buildingKey) return;
        setLoading(true);
        setError(null);
        try {
            const fetched = await buildingExternalContactsApi.listForBuilding(buildingKey);
            setContacts(fetched);
        } catch (err) {
            console.error('Error fetching building external contacts:', err);
            setError('Kunde inte ladda externa kontakter');
        } finally {
            setLoading(false);
        }
    }, [buildingKey]);

    useEffect(() => {
        fetchContacts();
    }, [fetchContacts]);

    const addContact = useCallback(
        async (externalUserId: string, buildingNote?: string): Promise<void> => {
            setSubmitting(true);
            setError(null);
            try {
                await buildingExternalContactsApi.addBuildingExternalContact(
                    buildingKey,
                    externalUserId,
                    buildingNote
                );
                await fetchContacts();
            } catch (err) {
                console.error('Error adding building external contact:', err);
                const message = err instanceof Error ? err.message : 'Kunde inte lägga till kontakt';
                setError(message);
                throw err;
            } finally {
                setSubmitting(false);
            }
        },
        [buildingKey, fetchContacts]
    );

    const removeContact = useCallback(
        async (externalUserId: string): Promise<void> => {
            setSubmitting(true);
            setError(null);
            try {
                await buildingExternalContactsApi.removeBuildingExternalContact(
                    buildingKey,
                    externalUserId
                );
                await fetchContacts();
            } catch (err) {
                console.error('Error removing building external contact:', err);
                const message = err instanceof Error ? err.message : 'Kunde inte ta bort kontakt';
                setError(message);
                throw err;
            } finally {
                setSubmitting(false);
            }
        },
        [buildingKey, fetchContacts]
    );

    const updateNote = useCallback(
        async (externalUserId: string, note: string): Promise<void> => {
            setSubmitting(true);
            setError(null);
            try {
                await buildingExternalContactsApi.updateBuildingExternalContactNote(
                    buildingKey,
                    externalUserId,
                    note
                );
                await fetchContacts();
            } catch (err) {
                console.error('Error updating building external contact note:', err);
                const message = err instanceof Error ? err.message : 'Kunde inte uppdatera anteckning';
                setError(message);
                throw err;
            } finally {
                setSubmitting(false);
            }
        },
        [buildingKey, fetchContacts]
    );

    const clearError = useCallback(() => setError(null), []);

    return {
        contacts,
        loading,
        error,
        submitting,
        refetch: fetchContacts,
        addContact,
        removeContact,
        updateNote,
        clearError,
    };
};
